// src/admin/pages/customers/customerService.ts
import {
  collection,
  doc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';

const CUSTOMERS_COLLECTION = 'customers';
const ORDERS_COLLECTION = 'orders';

export interface Customer {
  uid: string;
  name: string;
  email: string;
  phone?: string;
  photoURL?: string;
  isActive: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface CustomerOrderStats {
  orderCount: number;
  totalSpent: number;
}

export class CustomerService {
  // ── getAllCustomers ─────────────────────────────────────────────────────────
  // The `customers` collection is expected to stay small (registered shoppers,
  // not staff) — a single read + client-side search/filter/pagination keeps
  // this simple and avoids the "search only matches within the current page"
  // limitation that the server-paginated userService.getUsersPaginated has.
  static async getAllCustomers(): Promise<Customer[]> {
    try {
      const q = query(collection(db, CUSTOMERS_COLLECTION), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);

      return snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          uid: docSnap.id,
          name: data.name || '',
          email: data.email || '',
          phone: data.phone || undefined,
          photoURL: data.photoURL || undefined,
          isActive: data.isActive !== false,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        };
      });
    } catch (error: any) {
      console.error('Error fetching customers:', error);
      if (error.code === 'permission-denied') {
        throw new Error('You do not have permission to access customer data.');
      }
      throw error;
    }
  }

  // ── toggleCustomerStatus ─────────────────────────────────────────────────────
  // Disabling a customer here is enforced client-side on the storefront:
  // AuthContext.login() and the onAuthStateChanged bootstrap both re-check
  // isActive and sign the customer straight back out if it's false.
  static async toggleCustomerStatus(uid: string, isActive: boolean): Promise<void> {
    try {
      await updateDoc(doc(db, CUSTOMERS_COLLECTION, uid), {
        isActive,
        updatedAt: serverTimestamp(),
      });
    } catch (error: any) {
      console.error('Error toggling customer status:', error);
      if (error.code === 'permission-denied') {
        throw new Error('You do not have permission to update customer status');
      }
      throw error;
    }
  }

  // ── getCustomerOrderStats ────────────────────────────────────────────────────
  // Orders link to a customer by email (there is no customerId field on orders
  // yet), so this matches the same way the storefront's own order-history page
  // does for a logged-in shopper.
  static async getCustomerOrderStats(email: string): Promise<CustomerOrderStats> {
    if (!email) return { orderCount: 0, totalSpent: 0 };
    try {
      const q = query(collection(db, ORDERS_COLLECTION), where('customerEmail', '==', email));
      const snapshot = await getDocs(q);
      let totalSpent = 0;
      snapshot.docs.forEach((docSnap) => {
        totalSpent += docSnap.data().totalAmount || 0;
      });
      return { orderCount: snapshot.size, totalSpent };
    } catch (error) {
      console.error('Error fetching customer order stats:', error);
      return { orderCount: 0, totalSpent: 0 };
    }
  }
}
